import React, { useEffect, useContext } from 'react';
import { AppContext } from '../../../context/Context';
import { useTranslation } from 'react-i18next';
import '../../../assets/css/buttons.css';
import { SocketContext } from '../../../context/SocketContext';
import toast from 'react-hot-toast';
import { TextField, Typography } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material/';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SavingsIcon from '@mui/icons-material/Savings';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { formatCash } from '../../../utils/formatCash';
import {
  verticallyCenteredLabelSx,
  fieldHelpSx,
} from '../../../theme/fieldTokens';
import CashField from '../../AvslutteSkiftet/CashField';

function ModalStartShift() {
  const { t } = useTranslation();

  const {
    isFirstShiftOfTheDay,
    updateIsFirstShiftOfTheDay,
    previousShiftClose,
    session,
    updateFocusOnMainScreen,
    userInfo,
    operator,
    hall,
    currentUser,
    currentUserID,
    updateIsCashoutPending,
    openSessionTerminal,
    updateShowModalStartShift,
    logoutUser,
    resetTicketsCH,
    resetTransactions,
    updateUserLogin,
    updateCurrentUser,
    updateCurrentUserID,
    updateUserInfo,
    updateIsFullUser,
    updateIsAnonUser,
    updateIsHybridUser,
    updateIsFastUserLogin,

    updateIsPaymentOpen,
    updateIsSellerOpen,
    updatePaymentType,

    updateTotal,
    updatePayCard,
    updatePayCash,

    updateChange,
    updateIsCard,
    updateIsCash,

    updateUserChildren,
    updateUserParent,
    updateUserSiblings,
    updateAdoptedTicket,
    updateShowUserInfoSibling,
  } = useContext(AppContext);

  // Empty, not 0: the existing validation only fires on a blank field
  const [cashOnOpening, setCashOnOpening] = React.useState('');
  const [openingComment, setOpeningComment] = React.useState('');
  const [step, setStep] = React.useState('form');

  //* DISABLE FOCUS ON MAIN SCREEN AND PREVENT ESCAPE BUTTON
  useEffect(() => {
    updateFocusOnMainScreen(false);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  //* SOCKET SETUP
  const { socket } = useContext(SocketContext);

  useEffect(() => {
    const handleOpenShift = (data) => {
      if (!data) {
        toast.error(t('error.error'));
        return;
      }

      // Server recomputes the gap, so it can refuse what the screen accepted
      if (data.ok === false) {
        toast.error(t('sales.openingCommentRequired'));
        setStep('confirm');
        return;
      }

      updateShowModalStartShift(false);
      updateIsFirstShiftOfTheDay(false);
      updateFocusOnMainScreen(true);
    };
    socket.on('openShift', handleOpenShift);

    return () => {
      socket.off('openShift', handleOpenShift);
    };
  }, [socket]);

  const countedNumber = cashOnOpening === '' ? null : Number(cashOnOpening);
  const previousCounted =
    previousShiftClose?.cashCounted == null
      ? null
      : Number(previousShiftClose.cashCounted);

  // What the outgoing shift signed for vs what is actually in the drawer
  const openingAvvik =
    countedNumber != null && previousCounted != null
      ? countedNumber - previousCounted
      : null;

  const hasOpeningAvvik = openingAvvik !== null && openingAvvik !== 0;

  // Every shift counts now, not only the first of the day
  const handleConfirm = async () => {
    if (cashOnOpening === '' || cashOnOpening === null) {
      toast.error(t('error.errorCashOnOpening'));
      return;
    }
    setStep('confirm');
  };

  // Clears the figure: a recount that keeps the number is not a recount
  const handleRecount = () => {
    setCashOnOpening('');
    setOpeningComment('');
    setStep('form');
  };

  const emitOpenShift = () => {
    if (openingAvvik !== null && openingAvvik !== 0 && !openingComment.trim()) {
      toast.error(t('sales.openingCommentRequired'));
      return;
    }

    socket.emit('openShift', {
      bingoName: hall,
      operator: operator,
      sessionID: session,
      cashOnOpening: countedNumber,
      openingComment: openingComment.trim() || null,
    });
  };

  return (
    <div id="react-confirm-alert">
      <div className="react-confirm-alert-overlay">
        <div className="react-confirm-alert">
          <div
            className="react-confirm-alert-body modal-status"
            style={{ width: '820px' }}
          >
            {step === 'confirm' ? (
              <>
                <div className="modal-status-emblem tone-alert">
                  <SavingsIcon />
                </div>
                <h1>{t('sales.openShiftConfirmTitle')}</h1>

                <div className="modal-status-amount-label">
                  {t('sales.openShiftAmountLabel')}
                </div>
                <div className="modal-status-amount">
                  {formatCash(cashOnOpening) || '0'}
                  <span>kr</span>
                </div>

                {/* Revealed only here, after the count is committed. Shown on
                    the form step it was the answer sheet. */}
                {previousCounted !== null && (
                  <div className="modal-status-handover">
                    <div className="modal-status-meta">
                      <span>
                        {t('sales.handoverTitle')}:
                        <strong>{formatCash(previousCounted)} kr</strong>
                      </span>
                      <span>
                        {t('sales.handoverSignedBy')}:
                        <strong>
                          {previousShiftClose.signedBy ||
                            previousShiftClose.operator ||
                            '—'}
                        </strong>
                      </span>
                      {previousShiftClose.closedAt && (
                        <span>
                          {t('sales.handoverClosedAt')}:
                          <strong>
                            {previousShiftClose.closedAt.slice(11, 16)}
                          </strong>
                        </span>
                      )}
                    </div>

                    {openingAvvik !== null && (
                      <div
                        className="modal-status-handover-avvik"
                        style={{
                          color: openingAvvik === 0 ? '#5ad19b' : '#fcad89',
                          fontSize: '22px',
                        }}
                      >
                        {openingAvvik === 0
                          ? t('sales.openingMatches')
                          : `${t('sales.openingAvvik')}: ${
                              openingAvvik > 0 ? '+' : ''
                            }${openingAvvik.toLocaleString('nb-NO')} kr`}
                      </div>
                    )}
                  </div>
                )}

                {openingAvvik !== null && openingAvvik !== 0 ? (
                  <>
                    <TextField
                      autoComplete="off"
                      label={t('sales.openingCommentLabel') + '*'}
                      value={openingComment}
                      onChange={(e) => setOpeningComment(e.target.value)}
                      variant="outlined"
                      multiline
                      maxRows={4}
                      inputProps={{ maxLength: 500 }}
                      // Typed text stays left: it is a sentence, not a figure
                      InputLabelProps={{ sx: verticallyCenteredLabelSx }}
                      className="modal-textfield"
                      style={{ width: '100%', marginTop: '20px' }}
                    />
                    <Typography sx={fieldHelpSx}>
                      {t('sales.openingCommentHelp')}
                    </Typography>
                  </>
                ) : (
                  <p>{t('sales.openShiftCountedQuestion')}</p>
                )}

                <div
                  className="react-confirm-alert-button-group"
                  style={{ marginTop: '42px' }}
                >
                  <button
                    className="btn-custom-yes"
                    onClick={() => {
                      emitOpenShift();
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <CheckCircleIcon style={{ fontSize: '1.2em' }} />
                    {t('sales.confirm')}
                  </button>

                  {/* One way back: recount clears the figure, correction keeps it */}
                  <button
                    className={'btn-custom-back'}
                    onClick={
                      hasOpeningAvvik ? handleRecount : () => setStep('form')
                    }
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <ArrowBackIcon style={{ fontSize: '1.2em' }} />
                    {hasOpeningAvvik
                      ? t('sales.openingRecount')
                      : t('sales.openShiftBack')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="modal-status-emblem tone-positive">
                  <StorefrontIcon />
                </div>
                <h1>{t('sales.openShiftTitle')} </h1>
                <p>{t('sales.openShiftText')}</p>

                {/* Both values were already in context but never shown — worth a
                glance before opening a shift under the wrong operator. */}
                <div className="modal-status-meta">
                  <span>
                    {t('sales.openShiftOperator')}:<strong>{operator}</strong>
                  </span>
                  <span>
                    {t('sales.openShiftHall')}:<strong>{hall}</strong>
                  </span>
                </div>

                {/* Nothing about the previous close on this step — the count
                    has to be blind, and the handover figure is the answer. */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: '26px',
                  }}
                >
                  <CashField
                    label={t('sales.cashOnOpening') + '*'}
                    value={cashOnOpening}
                    onChange={setCashOnOpening}
                    autoFocus
                  />
                </div>

                <div
                  className="react-confirm-alert-button-group"
                  style={{ marginTop: '42px' }}
                >
                  <button
                    className="btn-custom-yes"
                    onClick={() => {
                      handleConfirm();
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px', // Space between icon and text
                    }}
                  >
                    <CheckCircleIcon style={{ fontSize: '1.2em' }} />

                    {t('sales.confirm')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ModalStartShift;
